import createOpenapiClient, { type Middleware } from 'openapi-fetch';
import { env } from '../config/env';
import {
  authService,
  redirectToLogin,
  type ApiAudience,
} from '../auth';
import { ApiError } from './errors';

export interface CreateApiClientOptions {
  baseUrl: string;
  /** Extra headers applied to every request. */
  headers?: Record<string, string>;
  /** Which backend API — picks the matching access JWT (`aud`). */
  audience: ApiAudience;
}

/** Apply audience-specific access JWT as `Authorization: Bearer`. */
export function applyApiAuthHeaders(headers: Headers, audience: ApiAudience): void {
  const token = authService.getAccessToken(audience);
  if (!token) return;
  headers.set('Authorization', `Bearer ${token}`);
}

async function throwIfNotOk(response: Response): Promise<void> {
  if (response.ok) return;
  let body: unknown = null;
  try {
    const ct = response.headers.get('content-type') || '';
    body = ct.includes('application/json')
      ? await response.clone().json()
      : await response.clone().text();
  } catch {
    /* ignore parse error */
  }
  throw new ApiError(`HTTP ${response.status} ${response.statusText}`, response.status, body);
}

/**
 * Raw fetch with audience Bearer token and a single 401 → re-exchange retry.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  audience: ApiAudience,
): Promise<Response> {
  await authService.ensureAccessToken(audience);
  const headers = new Headers(init?.headers);
  applyApiAuthHeaders(headers, audience);
  const res = await fetch(input, { ...init, headers });
  if (res.status !== 401 || !env.authEnabled) return res;

  const ok = await authService.ensureAccessToken(audience, true);
  if (!ok) {
    await redirectToLogin();
    return res;
  }
  const retryHeaders = new Headers(init?.headers);
  applyApiAuthHeaders(retryHeaders, audience);
  const retried = await fetch(input, { ...init, headers: retryHeaders });
  if (retried.status === 401) {
    await redirectToLogin();
  }
  return retried;
}

/**
 * Creates an openapi-fetch client with shared middleware:
 *   - `Authorization: Bearer` for the given audience
 *   - 401 → force token exchange and retry once
 *   - Throw ApiError on non-2xx
 */
export function createApiClient<Paths extends object>(opts: CreateApiClientOptions) {
  const { audience } = opts;
  const client = createOpenapiClient<Paths>({
    baseUrl: opts.baseUrl,
    headers: opts.headers,
  });

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      await authService.ensureAccessToken(audience);
      applyApiAuthHeaders(request.headers, audience);
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status === 401 && env.authEnabled) {
        const ok = await authService.ensureAccessToken(audience, true);
        if (ok) {
          const retry = request.clone();
          applyApiAuthHeaders(retry.headers, audience);
          const retried = await fetch(retry);
          if (retried.status === 401) {
            await redirectToLogin();
          }
          await throwIfNotOk(retried);
          return stripBogusEmptyContentLength(retried);
        }
        await redirectToLogin();
      }
      await throwIfNotOk(response);
      return stripBogusEmptyContentLength(response);
    },
  };

  client.use(authMiddleware);
  return client;
}

/**
 * openapi-fetch skips JSON parsing when `Content-Length: 0` (see its empty-content
 * short-circuit). Some proxies send that header while still streaming a JSON body;
 * DevTools shows the body, but `data` becomes `undefined`. Drop the bad header so
 * the body is parsed.
 */
function stripBogusEmptyContentLength(response: Response): Response {
  const cl = response.headers.get('Content-Length');
  // Proxies sometimes send CL:0 (or empty) while the body still has JSON.
  if (cl !== '0' && cl !== '') return response;
  if (cl === null) return response;
  const ct = response.headers.get('content-type') || '';
  if (!ct.includes('application/json') && !ct.includes('text/json')) return response;
  const headers = new Headers(response.headers);
  headers.delete('Content-Length');
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

export type ApiClient<Paths extends object> = ReturnType<typeof createApiClient<Paths>>;
